// Settings → "PIN persetujuan".
//
// The owner sets a 4–6 digit PIN once. A cashier who needs to void a sale
// asks the owner to type it at the till; the server checks it and records the
// owner as the approver. The PIN is never displayed — only whether one is set
// and since when.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { Card, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { getOwnPinStatus, setOwnPin } from '@/lib/api/pin';
import { formatDateTime } from '@/lib/format';

const KEY = ['own-pin-status'] as const;

export function ApprovalPinForm() {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: KEY, queryFn: getOwnPinStatus });
  const save = useMutation({
    mutationFn: (pin: string) => setOwnPin(pin),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const valid = /^\d{4,6}$/.test(pin);

  const submit = async () => {
    setError(null);
    setDone(false);
    if (!valid) return setError('PIN harus 4–6 digit angka.');
    if (pin !== pin2) return setError('Ulangi PIN tidak sama.');
    try {
      await save.mutateAsync(pin);
      setPin('');
      setPin2('');
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan PIN');
    }
  };

  return (
    <Card>
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <KeyRound size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle>PIN persetujuan</CardTitle>
            {!isLoading && (
              <Chip variant={status?.set ? 'success' : 'warning'}>
                {status?.set ? 'Sudah diset' : 'Belum diset'}
              </Chip>
            )}
          </div>
          <CardDescription>
            Dipakai saat kasir perlu <b>void</b> transaksi: kamu ketik PIN ini di layar kasir,
            sistem mencatat kamu sebagai yang menyetujui. PIN tidak pernah ditampilkan.
            {status?.setAt ? ` Terakhir diubah ${formatDateTime(status.setAt)}.` : ''}
          </CardDescription>
        </div>
      </div>

      <form
        className="space-y-4 max-w-sm"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Input
          label={status?.set ? 'PIN baru' : 'PIN'}
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          hint="4–6 digit angka. Jangan pakai tanggal lahir atau 1234."
        />
        <Input
          label="Ulangi PIN"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={6}
          value={pin2}
          onChange={(e) => setPin2(e.target.value.replace(/\D/g, ''))}
        />
        {error && <p role="alert" className="text-caption text-danger">{error}</p>}
        {done && (
          <p className="inline-flex items-center gap-1.5 text-caption text-success">
            <ShieldCheck size={14} /> PIN tersimpan.
          </p>
        )}
        <Button type="submit" variant="primary" loading={save.isPending} disabled={!valid || pin !== pin2}>
          {status?.set ? 'Ganti PIN' : 'Simpan PIN'}
        </Button>
      </form>
    </Card>
  );
}
